import type { ReactElement, ReactNode } from "react"
import { ResponsiveContainer, Tooltip, type TooltipContentProps } from "recharts"
import { cn } from "@/lib/utils"

/**
 * shadcn/ui Chart composition, kept local in the same way as other copied
 * shadcn primitives in this app. Recharts owns plotting and responsiveness.
 */
export function ChartContainer({ className, children }: { className?: string; children: ReactElement }) {
  return (
    <div data-slot="chart" className={cn("h-64 w-full text-xs", className)}>
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  )
}

export const ChartTooltip = Tooltip

export function ChartTooltipContent({
  active,
  payload,
  label,
  formatter,
}: Omit<Partial<TooltipContentProps<number, string>>, "formatter"> & {
  formatter?: (value: number, name: string) => ReactNode
}) {
  if (!active || !payload?.length) return null
  return (
    <div className="min-w-36 rounded-md border border-border bg-popover px-2.5 py-2 text-xs shadow-lg">
      {label ? <div className="mb-1.5 font-medium">{String(label)}</div> : null}
      <div className="space-y-1">
        {payload.map((item) => (
          <div key={`${item.dataKey}`} className="flex items-center justify-between gap-4">
            <span className="text-muted-foreground">{item.name}</span>
            <span className="tabular-nums">
              {formatter && typeof item.value === "number"
                ? formatter(item.value, String(item.name ?? ""))
                : String(item.value ?? "—")}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
